import { useState, useEffect } from 'react';
import { FileText, Plus, ChevronRight, AlertCircle, Euro, Calendar, Building } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SubsidyDetailModal } from './SubsidyDetailModal';
import { FundCallDetailModal } from './FundCallDetailModal';
import { CreateSubsidyModal } from './CreateSubsidyModal';

interface Subsidy {
  id: string;
  name: string;
  funder: string;
  allocated_amount: number;
  start_date: string;
  end_date: string;
  status: 'en_cours' | 'cloturee';
  notes: string;
  created_at: string;
}

export function GrantsTab() {
  const [subsidies, setSubsidies] = useState<Subsidy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubsidy, setSelectedSubsidy] = useState<Subsidy | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadSubsidies();
  }, []);

  const loadSubsidies = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('grant_subsidies')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubsidies(data || []);
    } catch (error) {
      console.error('Error loading subsidies:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Traçabilité des Subventions</h2>
          <p className="mt-1 text-sm text-gray-600">
            Module documentaire de suivi des subventions et justificatifs (sans impact trésorerie)
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouvelle subvention
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Module de traçabilité uniquement</strong> - Ce module n'impacte pas vos soldes de trésorerie,
          votre plan de trésorerie ni vos indicateurs financiers. Il sert uniquement à documenter et justifier
          l'utilisation des subventions auprès des financeurs.
        </div>
      </div>

      {/* Subsidies list */}
      {subsidies.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune subvention</h3>
          <p className="text-gray-600 mb-4">
            Commencez par créer une subvention pour suivre vos appels de fonds et justificatifs.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Créer ma première subvention
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subvention
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Financeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant attribué
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subsidies.map((subsidy) => (
                  <tr key={subsidy.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-emerald-600" />
                        <div>
                          <div className="font-medium text-gray-900">{subsidy.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building className="w-4 h-4" />
                        {subsidy.funder || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <Euro className="w-4 h-4" />
                        {subsidy.allocated_amount ? formatCurrency(subsidy.allocated_amount) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <div>
                          {subsidy.start_date && subsidy.end_date
                            ? `${formatDate(subsidy.start_date)} - ${formatDate(subsidy.end_date)}`
                            : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          subsidy.status === 'en_cours'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {subsidy.status === 'en_cours' ? 'En cours' : 'Clôturée'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedSubsidy(subsidy)}
                        className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        Détails
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateSubsidyModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadSubsidies();
          }}
        />
      )}

      {selectedSubsidy && (
        <SubsidyDetailModal
          subsidy={selectedSubsidy}
          onClose={() => setSelectedSubsidy(null)}
          onUpdate={() => {
            loadSubsidies();
            setSelectedSubsidy(null);
          }}
        />
      )}
    </div>
  );
}
